import React from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FloatingAIChat from './FloatingAIChat';
import { OnboardingProvider } from '../contexts/OnboardingContext';
import OnboardingEntryScreen from './OnboardingEntryScreen';
import OnboardingOverlay from './OnboardingOverlay';

const NAV = [
  {
    section: 'Overzicht',
    items: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Afspraken', path: '/appointments' },
      { label: 'FAQ Inbox', path: '/faq' },
    ]
  },
  {
    section: 'AI-assistent',
    items: [
      { label: 'AI Chat', path: '/chat' },
      { label: 'Nieuwsbrieven & marketing', path: '/newsletter' },
      { label: 'Reisvoorstellen', path: '/proposals' },
    ]
  },
  {
    section: 'Klanten & reizen',
    items: [
      { label: 'Reisbescheiden', path: '/reisbescheiden' },
      { label: 'Boekingen', path: '/bookings' },
      { label: 'Klanten', path: '/klantinzichten' },
    ]
  },
  {
    section: 'Beheer',
    items: [
      { label: 'Kennisbank', path: '/knowledge', adminOnly: true },
      { label: 'Instellingen', path: '/settings' },
    ]
  }
];

export default function AppShell() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isOnChatPage = location.pathname === '/chat';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user?.agent_name
    ? user.agent_name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : (user?.email ? user.email.slice(0, 2).toUpperCase() : 'TA');

  return (
    <OnboardingProvider>
      <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg-page)' }}>
        {/* Sidebar */}
        <aside
          className="sidebar-gradient flex-shrink-0 flex flex-col"
          style={{ width: 208, overflow: 'hidden' }}
        >
          {/* Brand */}
          <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#fff', letterSpacing: '-0.02em' }}>
              TravelAIgent
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
              {user?.agency_name || 'Mijn Reisburo'}
            </div>
          </div>

          {/* Nav groups */}
          <nav style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {NAV.map(group => {
              const items = group.items.filter(item => !item.adminOnly || isAdmin);
              if (items.length === 0) return null;
              return (
                <div key={group.section} style={{ marginBottom: 4 }}>
                  <div style={{
                    fontSize: 10, fontWeight: 500, color: 'rgba(255,255,255,0.35)',
                    textTransform: 'uppercase', letterSpacing: '0.07em',
                    padding: '8px 8px 3px'
                  }}>
                    {group.section}
                  </div>
                  {items.map(item => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      style={({ isActive }) => ({
                        display: 'flex',
                        alignItems: 'center',
                        padding: '6px 8px',
                        borderRadius: 6,
                        fontSize: 12,
                        textDecoration: 'none',
                        marginBottom: 1,
                        color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                        background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                        fontWeight: isActive ? 500 : 400,
                      })}
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              );
            })}
          </nav>

          {/* Footer — user profile + account link */}
          <div style={{
            padding: 10,
            borderTop: '1px solid rgba(255,255,255,0.08)',
            flexShrink: 0,
          }}>
            <button
              onClick={() => navigate('/account')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: '100%', padding: '6px 4px',
                borderRadius: 6, border: 'none', cursor: 'pointer',
                background: 'transparent',
                textAlign: 'left',
              }}
              className="hover:bg-white/[0.07] transition-colors"
            >
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 600, color: '#fff',
                flexShrink: 0,
              }}>
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.agent_name || user?.email || 'Agent'}
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Account & billing</div>
              </div>
            </button>
            <button
              onClick={handleLogout}
              style={{
                marginTop: 2, width: '100%',
                fontSize: 11, color: 'rgba(255,255,255,0.4)',
                background: 'none', border: 'none', cursor: 'pointer',
                textAlign: 'left', padding: '4px 8px',
                borderRadius: 6,
              }}
              className="hover:text-white/70 transition-colors"
            >
              Uitloggen
            </button>
          </div>
        </aside>

        {/* Main content area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          <main style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <Outlet />
          </main>
        </div>
      </div>

      {!isOnChatPage && <FloatingAIChat />}
      <OnboardingEntryScreen />
      <OnboardingOverlay />
    </OnboardingProvider>
  );
}
